<template>
    <div>
        <event-listener @on-click="handleBubbleOpen" @on-mouse-over="handleBubbleOpen">
            <slot/>
        </event-listener>
    </div>
</template>

<script>
import Vue from 'vue'
import EventListener from '@/helpers/EventListener'
import get from 'lodash-es/get'
import { mapGetters } from 'vuex'

export default {
    name: 'HereInfoBubble',
    components: { EventListener },
    inject: ['map', 'platform', 'ui'],
    props: {
        name: {
            type: String,
            required: true
        },
        lat: {
            type: Number,
            required: true
        },
        lng: {
            type: Number,
            required: true
        },
        component: {
            required: true
        },
        dataKey: {
            type: String,
            required: false,
            default: 'clusterData'
        },
        distanceUnit: {
            type: String,
            required: false
        },
        data: {
            required: false
        },
        hideClose: {
            type: Boolean,
            required: false,
            default: false
        },
        marginBottom: {
            type: Boolean,
            required: false,
            default: false
        },
        closeOnZoom: {
            type: Boolean,
            required: false,
            default: false
        },
        mouseOutTimer: {
            type: Number,
            required: false,
            default: 500
        },
        isMultiEvents: {
            type: Boolean,
            required: false,
            default: false
        },
        isClusteringBubble: {
            type: Boolean,
            required: false,
            default: false
        },
        onClearClusterBubbleData: {
            type: Function,
            required: false,
            default: () => {}
        }
    },
    data () {
        return {
            infoBubble: null,
            zoomLevel: null,
            timeoutId: null,
            bubbleComponent: null
        }
    },
    computed: {
        latLng () {
            return `${this.lat}|${this.lng}`
        },
        ...mapGetters({
            showRawData: 'trackingModule/showRawData'
        })
    },
    watch: {
        latLng (newVal, oldVal) {
            const [oldLat, oldLng] = oldVal.split('|')
            const [newLat, newLng] = newVal.split('|')

            if ((null != newLat && null != newLng) && (oldLat !== newLat && oldLng !== newLng)) {
                if (null != this.lat && null != this.lng && null != this.infoBubble) {
                    this.infoBubble.setPosition({
                        lat: this.lat,
                        lng: this.lng
                    })
                }
            }
        },
        'data.event': {
            handler (newVal) {
                if (newVal) {
                    this.handleBubbleOpen()
                }
            },
            deep: true
        }
    },
    mounted () {
        if (this.isClusteringBubble) {
            this.handleBubbleOpen()
        }

        this.$nextTick(() => {
            if (this.closeOnZoom && this.map()) {
                this.map().addEventListener(
                    'mapviewchangestart',
                    this.handleZoomChange
                )
            }
        })
    },
    updated () {
        if (null != this.lat && null != this.lng && null != this.infoBubble) {
            this.infoBubble.setPosition({
                lat: this.lat,
                lng: this.lng
            })
        }
    },
    methods: {
        getData (entity) {
            let data
            if (null == entity) data = null
            if ('function' === typeof entity.getData) data = entity.getData()
            // At this point we will have data assigned to either:
            // - The custom data assigned to an object.
            // - A wrapper object in the case of a cluster.
            if (null != data && 'function' === typeof data.getData) data = data.getData()
            if (null != data && 'function' === typeof data.forEachDataPoint) {
                const nextData = []
                data.forEachDataPoint(p => nextData.push(p.getData()))
                data = nextData
            }

            return data
        },

        /**
         * Provide a consistent method for returning the lat lng of an object.
         */
        getLocation (object) {
            if (null != this.lat && null != this.lng) return { lat: this.lat, lng: this.lng }
            // eslint-disable-next-line no-undef
            if (object instanceof H.map.Circle) return object.getCenter()
            // eslint-disable-next-line no-undef
            if (object instanceof H.map.Marker) return object.getPosition()
            return null
        },

        /**
         * Fired when `closeOnZoom` is set to `true` and
         * the map zoom level changes.
         */
        handleZoomChange (evt) {
            const nextZoomLevel = evt.target.getZoom()

            if (null != this.infoBubble && (null == this.zoomLevel || this.zoomLevel !== this.nextZoomLevel) && this.ui()) {
                this.ui().removeBubble(this.infoBubble)

                this.infoBubble = null
                this.zoomLevel = nextZoomLevel
            }
        },

        /**
         * Event handler called when the wrapped Here component wishes to open
         * an InfoBubble.
         */
        handleBubbleOpen (evt = { target: null }) {
            if (null == this.ui()) return

            // Check for an existing bubble with this name.
            // If found, remove it as we need to re-render.
            if (null != this.infoBubble) {
                const container = document.getElementById(this.name)

                if (null != container) {
                    this.bubbleComponent.$destroy()
                } else if (null != this.ui()) {
                    this.ui().removeBubble(this.infoBubble)
                }
            }

            // Remove any existing bubbles that share the same name.
            this.ui().getBubbles().forEach((b) => {
                if (get(b.getData(), 'name', null) !== this.name) return

                this.ui().removeBubble(b)
            })

            // The placeholder is used to attach the react component.
            // It should be entirely unique on the page, it may be
            // worth adding a warning here if a DOM element is found.
            const placeholder = `<div id="${this.name}"></div>`
            const point = this.getLocation(evt.target)
            if (null == point) return

            // eslint-disable-next-line no-undef
            const nextInfoBubble = new H.ui.InfoBubble(point, {
                content: placeholder
            })

            if (this.isClusteringBubble) {
                nextInfoBubble.addEventListener('statechange', (evt) => {
                    if (evt.target.getState() === H.ui.InfoBubble.State.CLOSED) {
                        this.onClearClusterBubbleData()
                    }
                })
            }

            nextInfoBubble.addClass('sp-info-bubble')

            if (this.marginBottom) {
                nextInfoBubble.addClass('sp-info-bubble--bottom-margin')
            }

            if (this.hideClose) nextInfoBubble.addClass('sp-info-bubble--hide-close')
            nextInfoBubble.setData({ name: this.name })

            // Add the bubble template to the ui - this will render it in the DOM.
            this.ui().addBubble(nextInfoBubble)

            this.infoBubble = nextInfoBubble

            let markerLocation

            if (evt.target) {
                markerLocation = (evt.target).getPosition
                    ? (evt.target).getPosition()
                    : null
            } else {
                markerLocation = null
            }


            // Get data for the bubble component.
            const entityData = evt.target ? this.getData(evt.target) : null
            const bubbleData = {
                ...this.data,
                ...(Array.isArray(entityData) ? { [this.dataKey || '']: entityData } : entityData),
                markerLocation
            }

            // Finally, render the Vue component provided in to the template DOM element.
            this.bubbleComponent = new Vue({
                ...this.component,
                propsData: {
                    ...bubbleData,
                    getSpeedUnit: this.$store.getters['authModule/getSpeedUnit']
                },
                listeners: {
                    focus: this.handleBubbleOver,
                    blur: this.handleBubbleOut,
                    mouseOver: this.handleBubbleOver,
                    mouseOut: this.handleBubbleOut
                }
            }).$mount(`#${this.name}`)
        },

        handleBubbleOver () {
            this.stopTimeout()
        },

        handleBubbleOut () {
            this.restartTimeout()
        },

        /**
         * Event handler called when the wrapped Here component wishes to close an InfoBubble.
         * Note: This is not called when clicking the dismiss icon in the InfoBubble.
         */
        handleBubbleClose () {
            this.restartTimeout()
        },

        restartTimeout () {
            if (null == this.infoBubble) return

            this.stopTimeout()
            this.startTimeout()
        },

        stopTimeout () {
            if (null == this.timeoutId) return

            clearTimeout(this.timeoutId)
        },

        startTimeout () {
            if (null == this.infoBubble || null == this.ui()) return

            this.timeoutId = window.setTimeout(() => {
                if (null != this.infoBubble) this.ui().removeBubble(this.infoBubble)

                this.infoBubble = null
                this.timeoutId = null
            }, this.mouseOutTimer)
        }
    }
}
</script>

<style lang="scss">
// Styles have to be unscoped to properly override HereMaps InfoBubble styles

.sp-info-bubble {
    &--hide-close {
        .H_ib_close {
            display: none;
        }
    }
    .H_ib_body {
        padding: 0;
    }
    .H_ib_close {
        top: 6px;
        right: 6px;
        height: 8px;
        width: 8px;

        svg {
            fill: black !important;
        }
    }
    // Override Here UI
    * {
        box-sizing: border-box;
    }

    .H_ib {
        &_body {
            transform: translate(40%, -10px);
            background-color: rgba(255,255,255, 0.9);
            color: $dark;
            box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.3);
        }

        &_content {
            padding: 0;
            margin: 0;
        }

        &_close .H_icon {
            fill: $dark;
        }

        &_tail {
            display: none;
        }
    }
    &--bottom-margin {
        .H_ib_body {
            margin-bottom: 3.25em;
            pointer-events: none;
        }
    }
}
</style>
